import { Stack, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { EOp, FilterModel, Nullish } from 'types'

type StatusOption = Nullish<{ value: 'active' | 'unsubscribed' | 'inactive'; label: string }>

const statusOptions: StatusOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'inactive', label: 'Inactive' },
]

/**
 * Statuses that still grant access. A paid subscriber who turned auto-renewal off stays
 * `unsubscribed` until the period ends, so "Active" keeps them and "Inactive" does not.
 */
const ACCESS_STATUSES = ['active', 'unsubscribed']

export function useCaregiverStatusFilter() {
  const [statusValue, setStatusValue] = useState<StatusOption>(null)

  const caregiverStatusFilterValue: Nullish<FilterModel> = useMemo((): Nullish<FilterModel> => {
    if (!statusValue) return null

    switch (statusValue.value) {
      case 'active':
        return { subscriptionStatus: { type: 'text', op: EOp.in, value: ACCESS_STATUSES } }
      case 'unsubscribed':
        return { subscriptionStatus: { type: 'text', op: EOp.equals, value: 'unsubscribed' } }
      default:
        return { subscriptionStatus: { type: 'text', op: EOp.notIn, value: ACCESS_STATUSES } }
    }
  }, [statusValue])

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: StatusOption) => {
      setStatusValue(newValue)
    },
    [setStatusValue],
  )

  const CaregiverStatusFilter = useMemo(() => {
    return (
      <Stack direction="row" alignItems="center" className="gap-8">
        <Typography>Status</Typography>

        <Autocomplete
          multiple={false}
          options={statusOptions}
          value={statusValue}
          onChange={handleChange}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              name="status"
              InputProps={{
                ...params.InputProps,
                endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
              }}
            />
          )}
        />
      </Stack>
    )
  }, [handleChange, statusValue])

  return { CaregiverStatusFilter, caregiverStatusFilterValue }
}
